import { useState, useRef, useMemo, useLayoutEffect } from 'react';
import { apiPatch } from '../../api/client';
import { type ConfigSaveResponse, saveResultMessage } from '../../lib/applyResult';
import { configSectionLabel } from '../../lib/definitions/settings';
import { isFieldValueSet, fieldDomId } from './schema';
import type { SchemaField } from './schema';
import { PaneDescription, FieldEditor } from './FieldEditor';
import { recordNounSingular, recordTitle, recordSubtitle, blankItemFromFields, newRowKey, listSectionSaveTarget } from './records';


// One row of master-detail state: `baseline` is frozen at row creation (the
// live value for an existing record, or the schema-default blank object for
// a newly added one) and never mutated — it's what drives the primary/
// secondary field split and the per-row dirty comparison. `current`
// is the live editable copy. Saving sends `current` for every row, in
// display order — full replace, per this file's save-contract comment.
interface RecordRow {
  key: string;
  isNew: boolean;
  baseline: Record<string, unknown>;
  current: Record<string, unknown>;
}


// Master-detail editor for a single list section, used identically by all
// six list sections (regions included — no longer special-cased). One
// record is shown in the detail pane at a time; the record list column is
// just titles/subtitles, which is what keeps a section with a dozen-plus
// fields per record from becoming a many-thousand-pixel scroll.
export function ListSectionEditor({
  path,
  description,
  itemFields,
  initialItems,
  highlightPath,
}: {
  path: string;
  description?: string;
  itemFields: SchemaField[];
  initialItems: Record<string, unknown>[];
  highlightPath: string | null;
}) {
  const [rows, setRows] = useState<RecordRow[]>(() =>
    initialItems.map((item, idx) => ({ key: `existing-${idx}`, isNew: false, baseline: item, current: { ...item } })),
  );
  const [savedSnapshot, setSavedSnapshot] = useState(() => JSON.stringify(initialItems));
  const [selectedKey, setSelectedKey] = useState<string | null>(rows[0]?.key ?? null);
  const [filterText, setFilterText] = useState("");
  const detailsRef = useRef<HTMLDetailsElement | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  const currentSnapshot = JSON.stringify(rows.map((r) => r.current));
  const isDirty = currentSnapshot !== savedSnapshot;

  const newRecordFallback = `New ${recordNounSingular(path)}`;
  const decorated = rows.map((row) => ({
    row,
    title: recordTitle(row.current, newRecordFallback),
  }));
  const filtered = decorated.filter(({ row, title }) => {
    if (!filterText.trim()) return true;
    const q = filterText.trim().toLowerCase();
    const subtitle = recordSubtitle(row.current, title) ?? "";
    return title.toLowerCase().includes(q) || subtitle.toLowerCase().includes(q);
  });

  const selected = rows.find((r) => r.key === selectedKey) ?? null;

  // Primary/secondary split is frozen against the row's BASELINE, not its
  // live current value — so filling in an originally-empty field doesn't
  // yank it out of "Optional details" mid-edit (see this file's header
  // comment). Missing key entirely (schema default in effect) is always
  // secondary, per the ground-truth note that a list item may omit keys
  // left at their default.
  const primaryKeys = useMemo(() => {
    if (!selected) return new Set<string>();
    const s = new Set<string>();
    for (const f of itemFields) {
      if (Object.prototype.hasOwnProperty.call(selected.baseline, f.path) && isFieldValueSet(selected.baseline[f.path])) {
        s.add(f.path);
      }
    }
    return s;
  }, [selected, itemFields]);

  const primaryFields = itemFields.filter((f) => primaryKeys.has(f.path));
  const secondaryFields = itemFields.filter((f) => !primaryKeys.has(f.path));
  const filledSecondaryCount = selected ? secondaryFields.filter((f) => isFieldValueSet(selected.current[f.path])).length : 0;

  // Uncontrolled by design: the <details> below never gets a React `open`
  // prop at all — a FULLY CONTROLLED version (an `open` prop driven by
  // React state, toggled via an `onToggle` handler) was tried first and
  // crashes ("Cannot read properties of null (reading 'open')"): setting
  // `open` programmatically fires a native `toggle` event synchronously
  // during React's own commit, and reading `e.currentTarget` from inside
  // that handler can see a null target. Instead, the initial state (open
  // for a newly added record, closed otherwise) and the one case that must
  // force it open later (a search match landing on a secondary field) are
  // both applied imperatively through a ref, only when the selected record
  // or the search target actually changes — never on every render, which
  // is what would otherwise fight a user's own manual toggle.
  useLayoutEffect(() => {
    if (!detailsRef.current || !selected) return;
    const forceOpenForMatch = !!highlightPath && secondaryFields.some((f) => f.path === highlightPath);
    detailsRef.current.open = selected.isNew || forceOpenForMatch;
    // Deliberately excludes secondaryFields/selected.isNew from the deps —
    // this must run exactly once per record switch or search-target
    // change, not on every edit to the selected record's own fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.key, highlightPath]);

  function updateField(rowKey: string, fieldPath: string, value: unknown) {
    setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, current: { ...r.current, [fieldPath]: value } } : r)));
    setSaveMsg(null);
  }

  function addRecord() {
    const blank = blankItemFromFields(itemFields);
    const key = newRowKey();
    setRows((prev) => [...prev, { key, isNew: true, baseline: blank, current: { ...blank } }]);
    setSelectedKey(key);
    setSaveMsg(null);
  }

  function removeSelected() {
    if (!selected) return;
    const idx = rows.findIndex((r) => r.key === selected.key);
    const next = rows.filter((r) => r.key !== selected.key);
    setRows(next);
    const neighborIdx = Math.min(idx, next.length - 1);
    setSelectedKey(next[neighborIdx]?.key ?? null);
    setSaveMsg(null);
  }

  async function onSave() {
    if (!isDirty) return;
    setSaving(true);
    setSaveMsg(null);
    // Save payload rule: send each row's CURRENT object as-is (preserving
    // whatever original keys/values it already carried, e.g. an "extra"
    // nested object hazards.sources doesn't declare in item_fields at all)
    // rather than expanding every item to the full set of schema defaults
    // — that would write defaults into config.yaml that were deliberately
    // left absent.
    const items = rows.map((r) => r.current);
    const { url, body } = listSectionSaveTarget(path, items);
    try {
      const resp = await apiPatch<ConfigSaveResponse>(url, body);
      setSaveMsg(saveResultMessage(resp?.applied));
      setRows((prev) => prev.map((r) => ({ ...r, baseline: r.current, isNew: false })));
      setSavedSnapshot(JSON.stringify(items));
    } catch (e) {
      setSaveMsg({ kind: "error", text: e instanceof Error ? e.message : "Save failed." });
    } finally {
      setSaving(false);
    }
  }

  const noun = recordNounSingular(path);

  return (
    <div className="settings-pane">
      {description && <PaneDescription text={description} label={configSectionLabel(path)} />}
      <div className="settings-record-toolbar">
        <input
          type="text"
          className="settings-record-filter"
          placeholder="Filter records…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          aria-label="Filter records"
        />
        <button type="button" className="settings-record-add-btn" onClick={addRecord}>
          + Add {noun}
        </button>
        {saveMsg && (
          <span className="settings-save-status" data-kind={saveMsg.kind}>
            {saveMsg.text}
          </span>
        )}
        <button className="settings-save-btn" onClick={onSave} disabled={saving || !isDirty}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="settings-record-split">
        <div className="settings-record-list">
          {filtered.length === 0 && <div className="settings-record-empty">No records match.</div>}
          {filtered.map(({ row, title }) => {
            const subtitle = recordSubtitle(row.current, title);
            const unsaved = row.isNew || JSON.stringify(row.current) !== JSON.stringify(row.baseline);
            return (
              <button
                key={row.key}
                type="button"
                className="settings-record-row"
                data-active={row.key === selectedKey ? "true" : "false"}
                onClick={() => setSelectedKey(row.key)}
              >
                <span className="settings-record-row-title">
                  {unsaved && <span className="settings-record-dot" title="Unsaved changes" />}
                  {title}
                </span>
                {subtitle && <span className="settings-record-row-subtitle">{subtitle}</span>}
              </button>
            );
          })}
        </div>

        <div className="settings-record-detail">
          {!selected && <div className="settings-record-empty">No record selected — add one to get started.</div>}
          {selected && (
            <>
              <div className="settings-record-detail-header">
                <h3>{recordTitle(selected.current, newRecordFallback)}</h3>
                <button type="button" className="settings-record-remove-btn" onClick={removeSelected}>
                  Remove
                </button>
              </div>

              <div className="settings-fields-grid">
                {primaryFields.map((f) => (
                  <div key={f.path} id={fieldDomId(path, f.path)}>
                    <FieldEditor
                      field={f}
                      value={selected.current[f.path] ?? f.default}
                      dirty={JSON.stringify(selected.baseline[f.path]) !== JSON.stringify(selected.current[f.path])}
                      highlighted={highlightPath === f.path}
                      onChange={(v) => updateField(selected.key, f.path, v)}
                    />
                  </div>
                ))}
              </div>

              <details key={selected.key} ref={detailsRef} className="settings-record-optional">
                <summary>
                  Optional details ({filledSecondaryCount} of {secondaryFields.length} set)
                </summary>
                <div className="settings-fields-grid">
                  {secondaryFields.map((f) => (
                    <div key={f.path} id={fieldDomId(path, f.path)}>
                      <FieldEditor
                        field={f}
                        value={selected.current[f.path] ?? f.default}
                        dirty={JSON.stringify(selected.baseline[f.path]) !== JSON.stringify(selected.current[f.path])}
                        highlighted={highlightPath === f.path}
                        onChange={(v) => updateField(selected.key, f.path, v)}
                      />
                    </div>
                  ))}
                </div>
              </details>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
